// components/admin/BookingManager.js
import  { useState, useEffect } from 'react';
import { useAdmin } from '../context/AdminContext';

const BookingManager = ({ setMessage }) => {
    const {
        getBookings,
        getBooking,
        updateBookingStatus,
        deleteBooking,
        getBookingStats
    } = useAdmin();

    const [bookings, setBookings] = useState([]);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        status: 'all',
        search: '',
        page: 1,
        limit: 10
    });
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        pages: 1
    });
    const [adminNotes, setAdminNotes] = useState('');
    const [showNotesModal, setShowNotesModal] = useState(false);

    useEffect(() => {
        loadBookings();
        loadStats();
    }, [filters]);

    const loadBookings = async () => {
        try {
            setLoading(true);
            const data = await getBookings(filters);
            setBookings(data.bookings || []);
            setPagination(data.pagination || {
                page: 1,
                limit: 10,
                total: 0,
                pages: 1
            });
        } catch (error) {
            console.error('Failed to load bookings:', error);
            setMessage({ type: 'error', text: 'Failed to load bookings' });
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const data = await getBookingStats();
            setStats(data.stats);
        } catch (error) {
            console.error('Failed to load stats:', error);
        }
    };

    const handleStatusChange = async (bookingId, newStatus) => {
        try {
            await updateBookingStatus(bookingId, newStatus, adminNotes);
            setMessage({ type: 'success', text: 'Booking status updated successfully' });
            setShowNotesModal(false);
            setAdminNotes('');
            loadBookings();
            loadStats();
        } catch (error) {
            console.error('Failed to update status:', error);
            setMessage({ type: 'error', text: 'Failed to update booking status' });
        }
    };

    const handleDelete = async (bookingId) => {
        if (window.confirm('Are you sure you want to delete this booking?')) {
            try {
                await deleteBooking(bookingId);
                setMessage({ type: 'success', text: 'Booking deleted successfully' });
                loadBookings();
                loadStats();
            } catch (error) {
                console.error('Failed to delete booking:', error);
                setMessage({ type: 'error', text: 'Failed to delete booking' });
            }
        }
    };

    const handleViewBooking = async (bookingId) => {
        try {
            const data = await getBooking(bookingId);
            setSelectedBooking(data.booking);
        } catch (error) {
            console.error('Failed to load booking:', error);
            setMessage({ type: 'error', text: 'Failed to load booking details' });
        }
    };

    const handlePageChange = (newPage) => {
        setFilters(prev => ({ ...prev, page: newPage }));
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatSimpleDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'pending': return 'bg-warning';
            case 'confirmed': return 'bg-success';
            case 'cancelled': return 'bg-danger';
            case 'completed': return 'bg-info';
            default: return 'bg-secondary';
        }
    };

    if (loading && !bookings.length) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading bookings...</p>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">
                    <i className="fas fa-calendar-check text-primary me-2"></i>
                    Booking Management
                </h4>
            </div>

            {/* Statistics Cards */}
            {stats && (
                <div className="row mb-4">
                    <div className="col-md-3 mb-3">
                        <div className="card bg-primary text-white">
                            <div className="card-body text-center">
                                <h5 className="card-title">Total Bookings</h5>
                                <h2>{stats.total}</h2>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 mb-3">
                        <div className="card bg-warning text-dark">
                            <div className="card-body text-center">
                                <h5 className="card-title">Pending</h5>
                                <h2>{stats.pending}</h2>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 mb-3">
                        <div className="card bg-success text-white">
                            <div className="card-body text-center">
                                <h5 className="card-title">Confirmed</h5>
                                <h2>{stats.confirmed}</h2>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 mb-3">
                        <div className="card bg-info text-white">
                            <div className="card-body text-center">
                                <h5 className="card-title">Completed</h5>
                                <h2>{stats.completed}</h2>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-3">
                            <label className="form-label">Status</label>
                            <select
                                className="form-select"
                                value={filters.status}
                                onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
                            >
                                <option value="all">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="confirmed">Confirmed</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="completed">Completed</option>
                            </select>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Search</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by name, email, or package..."
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Items per page</label>
                            <select
                                className="form-select"
                                value={filters.limit}
                                onChange={(e) => setFilters({ ...filters, limit: e.target.value, page: 1 })}
                            >
                                <option value="10">10 per page</option>
                                <option value="20">20 per page</option>
                                <option value="50">50 per page</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bookings Table */}
            <div className="card">
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th>Customer</th>
                                    <th>Package</th>
                                    <th>Date</th>
                                    <th>Status</th>
                                    <th>Booked On</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bookings.map((booking) => (
                                    <tr
                                        key={booking._id}
                                        className={booking.status === 'pending' ? 'table-warning' : ''}
                                    >
                                        <td>
                                            <div>
                                                <strong>{booking.name}</strong>
                                                <div className="text-muted small">{booking.email}</div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="text-truncate" style={{ maxWidth: '150px' }}>
                                                {booking.packageName || booking.destination}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="badge bg-light text-dark">
                                                {formatSimpleDate(booking.date)}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`badge ${getStatusBadgeClass(booking.status)}`}>
                                                {booking.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="text-muted small">
                                                {formatSimpleDate(booking.createdAt)}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="btn-group btn-group-sm">
                                                <button
                                                    className="btn btn-outline-primary"
                                                    onClick={() => handleViewBooking(booking._id)}
                                                    title="View Details"
                                                >
                                                    <i className="fas fa-eye"></i>
                                                </button>
                                                <button
                                                    className="btn btn-outline-success"
                                                    onClick={() => {
                                                        setSelectedBooking(booking);
                                                        setShowNotesModal(true);
                                                    }}
                                                    title="Update Status"
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                                <button
                                                    className="btn btn-outline-danger"
                                                    onClick={() => handleDelete(booking._id)}
                                                    title="Delete"
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* No bookings */}
                    {bookings.length === 0 && !loading && (
                        <div className="text-center py-5">
                            <i className="fas fa-calendar-times fa-3x text-muted mb-3"></i>
                            <h5 className="text-muted">No bookings found</h5>
                            <p className="text-muted">Try changing your filters</p>
                        </div>
                    )}

                    {/* Pagination */}
                    {pagination.pages > 1 && (
                        <nav className="mt-4">
                            <ul className="pagination justify-content-center">
                                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => handlePageChange(pagination.page - 1)}
                                        disabled={pagination.page === 1}
                                    >
                                        Previous
                                    </button>
                                </li>

                                {[...Array(pagination.pages)].map((_, i) => {
                                    const pageNum = i + 1;
                                    if (
                                        pageNum === 1 ||
                                        pageNum === pagination.pages ||
                                        (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2)
                                    ) {
                                        return (
                                            <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                                                <button
                                                    className="page-link"
                                                    onClick={() => handlePageChange(pageNum)}
                                                >
                                                    {pageNum}
                                                </button>
                                            </li>
                                        );
                                    } else if (
                                        pageNum === pagination.page - 3 ||
                                        pageNum === pagination.page + 3
                                    ) {
                                        return (
                                            <li key={pageNum} className="page-item disabled">
                                                <span className="page-link">...</span>
                                            </li>
                                        );
                                    }
                                    return null;
                                })}

                                <li className={`page-item ${pagination.page === pagination.pages ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => handlePageChange(pagination.page + 1)}
                                        disabled={pagination.page === pagination.pages}
                                    >
                                        Next
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    )}
                </div>
            </div>

            {/* Booking Details Modal */}
            {selectedBooking && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Booking Details</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setSelectedBooking(null)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <strong>Customer Name:</strong>
                                            <p className="mb-0">{selectedBooking.name}</p>
                                        </div>
                                        <div className="mb-3">
                                            <strong>Email:</strong>
                                            <p className="mb-0">
                                                <a href={`mailto:${selectedBooking.email}`} className="text-decoration-none">
                                                    {selectedBooking.email}
                                                </a>
                                            </p>
                                        </div>
                                        <div className="mb-3">
                                            <strong>Booking Date:</strong>
                                            <p className="mb-0">{formatDate(selectedBooking.createdAt)}</p>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <strong>Package/Destination:</strong>
                                            <p className="mb-0">{selectedBooking.packageName || selectedBooking.destination}</p>
                                        </div>
                                        <div className="mb-3">
                                            <strong>Travel Date:</strong>
                                            <p className="mb-0">
                                                <span className="badge bg-primary">
                                                    {formatSimpleDate(selectedBooking.date)}
                                                </span>
                                            </p>
                                        </div>
                                        <div className="mb-3">
                                            <strong>Status:</strong>
                                            <p className="mb-0">
                                                <span className={`badge ${getStatusBadgeClass(selectedBooking.status)}`}>
                                                    {selectedBooking.status}
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <strong>Special Request:</strong>
                                    {selectedBooking.message ? (
                                        <div className="border rounded p-3 bg-light mt-2">
                                            {selectedBooking.message}
                                        </div>
                                    ) : (
                                        <p className="text-muted mb-0">No special request</p>
                                    )}
                                </div>

                                {selectedBooking.adminNotes && (
                                    <div className="mb-3">
                                        <strong>Admin Notes:</strong>
                                        <div className="border rounded p-3 bg-warning bg-opacity-10 mt-2">
                                            {selectedBooking.adminNotes}
                                        </div>
                                    </div>
                                )}

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-2">
                                            <strong>IP Address:</strong>
                                            <p className="text-muted small mb-0">{selectedBooking.ipAddress || 'N/A'}</p>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-2">
                                            <strong>Last Updated:</strong>
                                            <p className="text-muted small mb-0">{formatDate(selectedBooking.updatedAt)}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setSelectedBooking(null)}
                                >
                                    Close
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => {
                                        setSelectedBooking(selectedBooking);
                                        setShowNotesModal(true);
                                    }}
                                >
                                    <i className="fas fa-edit me-2"></i>
                                    Update Status
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Update Status Modal */}
            {showNotesModal && selectedBooking && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Update Booking Status</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => {
                                        setShowNotesModal(false);
                                        setAdminNotes('');
                                    }}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Customer:</label>
                                    <p className="fw-bold">{selectedBooking.name}</p>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Package:</label>
                                    <p>{selectedBooking.packageName || selectedBooking.destination}</p>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Current Status:</label>
                                    <p>
                                        <span className={`badge ${getStatusBadgeClass(selectedBooking.status)}`}>
                                            {selectedBooking.status}
                                        </span>
                                    </p>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Update Status To:</label>
                                    <select
                                        className="form-select"
                                        value={selectedBooking.status}
                                        onChange={(e) => {
                                            setSelectedBooking({
                                                ...selectedBooking,
                                                status: e.target.value
                                            });
                                        }}
                                    >
                                        <option value="pending">Pending</option>
                                        <option value="confirmed">Confirmed</option>
                                        <option value="cancelled">Cancelled</option>
                                        <option value="completed">Completed</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Admin Notes (Optional):</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={adminNotes}
                                        onChange={(e) => setAdminNotes(e.target.value)}
                                        placeholder="Add any notes about this booking..."
                                    />
                                    <div className="form-text">
                                        These notes are only visible to admin
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setShowNotesModal(false);
                                        setAdminNotes('');
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => handleStatusChange(selectedBooking._id, selectedBooking.status)}
                                >
                                    Update Status
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookingManager;